import { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';

import { useFormatTimeOfDay } from '../util/displayTimeZone';

/**
 * A running clock over a video (VIDEO-CLOCK): the time of day of the playing
 * frame, in the portal's display time zone. The player already shows the
 * seconds into the clip; this shows when it happened.
 *
 * It reads the frame's own timestamp: the clip's `audio_sync_offset_s` is
 * when its first frame was, in seconds after the session started, so the
 * frame was at `startedAt + offset + currentTime` — the same mapping the
 * trace uses to seek the video, so the clock and the trace agree. A camera
 * that started late or stopped early is still right.
 *
 * Follows the video while it plays and when it is scrubbed. Put it inside a
 * `position: relative` box around the video.
 */
export function VideoClock({
  getVideo,
  startedAt,
  offsetS,
}: {
  getVideo: () => HTMLVideoElement | null | undefined;
  /** When the session started (ISO) — session time 0. */
  startedAt: string;
  /** video_time = session_time − offset. */
  offsetS: number;
}) {
  const fmt = useFormatTimeOfDay();
  const startMs = Date.parse(startedAt);
  const [label, setLabel] = useState<string | null>(null);
  // Callers pass a fresh getter on every render; the loop reads the latest
  // through a ref instead of restarting.
  const latest = useRef({ getVideo, offsetS, fmt, startMs });
  latest.current = { getVideo, offsetS, fmt, startMs };
  const hidden = !Number.isFinite(startMs);

  useEffect(() => {
    if (hidden) return;
    let raf = 0;
    let last = '';
    const tick = () => {
      const { getVideo: get, offsetS: off, fmt: f, startMs: t0 } = latest.current;
      const v = get();
      if (v) {
        const text = f(t0 + (off + v.currentTime) * 1000);
        if (text !== last) {
          last = text;
          setLabel(text);
        }
      }
      raf = requestAnimationFrame(tick);
    };
    tick();
    return () => cancelAnimationFrame(raf);
  }, [hidden]);

  if (hidden || label === null) return null;
  return (
    <Box
      data-testid="video-clock"
      aria-label={`Time of day: ${label}`}
      sx={{
        position: 'absolute',
        top: 8,
        left: 8,
        px: 0.75,
        py: 0.25,
        borderRadius: 0.75,
        bgcolor: 'rgba(0,0,0,0.6)',
        color: '#fff',
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
        fontSize: 13,
        fontVariantNumeric: 'tabular-nums',
        pointerEvents: 'none',
        userSelect: 'none',
        zIndex: 1,
      }}
    >
      {label}
    </Box>
  );
}
